import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import kit from "./kit.module.css";

const GAP = 6;
const EDGE = 12;

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

/**
 * A panel anchored to its trigger, drawn above the page.
 *
 * `Popover` sits inside its parent, so inside a table that scrolls in its own
 * box — every table does — a wide editor is cut off at the box's edge. This one
 * is drawn at the end of the page and kept inside the window: below the
 * trigger when there is room, above it when there is not. Same props as
 * `Popover`. Focus moves into it when it opens, and Escape puts focus back on
 * the trigger, so it stays usable from the keyboard.
 */
export function Floating({
  open,
  onClose,
  trigger,
  align = "left",
  wide = false,
  children,
}: {
  open: boolean;
  onClose: () => void;
  trigger: ReactNode;
  align?: "left" | "right";
  wide?: boolean;
  children: ReactNode;
}) {
  const anchor = useRef<HTMLSpanElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const box = panel.current;
      const at = anchor.current;
      if (!box || !at) return;
      const rect = at.getBoundingClientRect();
      const width = box.offsetWidth;
      const height = box.offsetHeight;
      const left = Math.max(EDGE, Math.min(align === "right" ? rect.right - width : rect.left, window.innerWidth - width - EDGE));
      const below = window.innerHeight - rect.bottom - GAP - EDGE;
      const above = rect.top - GAP - EDGE;
      const down = height <= below || below >= above;
      const room = Math.max(160, down ? below : above);
      box.style.maxHeight = `${room}px`;
      box.style.left = `${left}px`;
      box.style.top = `${down ? rect.bottom + GAP : Math.max(EDGE, rect.top - GAP - Math.min(height, room))}px`;
    };
    place();
    panel.current
      ?.querySelector<HTMLElement>("input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])")
      ?.focus({ preventScroll: true });
    const watcher = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    if (watcher && panel.current) watcher.observe(panel.current);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      watcher?.disconnect();
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      onClose();
      anchor.current?.querySelector<HTMLElement>("button, input")?.focus({ preventScroll: true });
    };
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchor.current?.contains(target) || panel.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown, true);
    };
  }, [open, onClose]);

  return (
    <span ref={anchor} className={kit.pop}>
      {trigger}
      {open &&
        createPortal(
          <div
            ref={panel}
            className={cx(kit.popPanel, wide && kit.popWide)}
            style={{ position: "fixed", top: -9999, left: -9999, right: "auto", bottom: "auto", zIndex: 1000, overflowY: "auto" }}
          >
            {children}
          </div>,
          document.body,
        )}
    </span>
  );
}
